import React, { useEffect, useRef, useState, useLayoutEffect } from "react";
import "./PromptToolsButton.css";
import wandIcon from "../public/img/prompt_genie/wand-magic-sparkles-light-full.svg";
import editIcon from "../public/img/prompt_genie/pen-to-square-light-full.svg";
import infoIcon from "../public/img/prompt_genie/circle-info-light-full.svg";
import caretRightIcon from "../public/img/prompt_genie/down_caret.svg";
import externalLinkIcon from "../public/img/prompt_genie/external-link-svgrepo-com.svg";
import improveIcon from "../public/img/prompt_genie/list-ol-light-full.svg";
import clarityIcon from "../public/img/prompt_genie/highlighter-light-full.svg";
import contextIcon from "../public/img/prompt_genie/brackets-square-light-full.svg";
import rocketIcon from "../public/img/prompt_genie/rocket-launch-light-full.svg";
import improveIconWhite from "../public/img/prompt_genie/list-ol-light-full_white.svg";
import clarityIconWhite from "../public/img/prompt_genie/highlighter-light-full_white.svg";
import contextIconWhite from "../public/img/prompt_genie/brackets-square-light-full_white.svg";
import rocketIconWhite from "../public/img/prompt_genie/rocket-launch-light-full_white.svg";
import plusWhite from "../public/img/prompt_genie/plus_white.svg";

const PromptToolsButton = ({ onClick, onAction }) => {
  const [open, setOpen] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(null);
  const [suppressContent, setSuppressContent] = useState(false);
  const [lockedWidth, setLockedWidth] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [submenu, setSubmenu] = useState(null); // 'make_better' | 'templates' | null
  const wrapperRef = useRef(null);
  const btnRef = useRef(null);
  const naturalWidthRef = useRef(null);

  useLayoutEffect(() => {
    // Measure immediately on mount to avoid first-open jump from auto -> 40px
    if (btnRef.current) {
      const w = btnRef.current.offsetWidth;
      if (w) setButtonWidth(w);
      if (!naturalWidthRef.current && w) naturalWidthRef.current = w;
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (open) {
          setSuppressContent(true);
          // Use the last measured natural width; avoid capturing 40px
          setLockedWidth(
            buttonWidth != null
              ? buttonWidth
              : naturalWidthRef.current != null
              ? naturalWidthRef.current
              : null
          );
          setIsAnimating(true);
          setOpen(false);
          setSubmenu(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open, buttonWidth]);

  // Measure the natural width of the button when in closed state (with text)
  useEffect(() => {
    const measure = () => {
      // Only update natural width when not animating and fully expanded (not circular)
      if (!open && !isAnimating && !suppressContent && btnRef.current) {
        const w = btnRef.current.offsetWidth;
        if (w && w > 40 && w !== buttonWidth) {
          setButtonWidth(w);
          if (!naturalWidthRef.current) naturalWidthRef.current = w;
        }
      }
    };
    // Measure on mount and when closing
    if (!open) {
      // Use rAF to ensure layout is settled
      requestAnimationFrame(measure);
    }
    // Optional: update on resize
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, isAnimating, suppressContent, buttonWidth]);

  const handleToggle = () => {
    if (!open) {
      setSuppressContent(true);
      if (!buttonWidth && btnRef.current) {
        const w = btnRef.current.offsetWidth;
        if (w) setButtonWidth(w);
        if (!naturalWidthRef.current && w) naturalWidthRef.current = w;
      }
      // Lock current natural width so we can animate to 40px smoothly
      const startWidth =
        buttonWidth != null
          ? buttonWidth
          : naturalWidthRef.current != null
          ? naturalWidthRef.current
          : btnRef.current
          ? btnRef.current.offsetWidth
          : null;
      setLockedWidth(startWidth);
      setIsAnimating(true);
      // Double rAF to ensure the browser paints the locked width before shrinking
      requestAnimationFrame(() => {
        if (btnRef.current) void btnRef.current.offsetWidth; // force reflow
        requestAnimationFrame(() => setOpen(true));
      });
    } else {
      setSuppressContent(true);
      // When closing, lock to measured natural width only
      setLockedWidth(
        buttonWidth != null
          ? buttonWidth
          : naturalWidthRef.current != null
          ? naturalWidthRef.current
          : null
      );
      setIsAnimating(true);
      setOpen(false);
      setSubmenu(null);
    }
    if (onClick) onClick();
  };

  const handlePromptTemplates = () => {
    // Close immediately
    setSubmenu(null);
    setSuppressContent(true);
    setLockedWidth(buttonWidth != null ? buttonWidth : naturalWidthRef.current);
    setIsAnimating(true);
    setOpen(false);
    // Fire action without waiting
    if (onAction) onAction("templates");
  };

  const handlePromptingTips = () => {
    // Open a placeholder external link in new tab for tips
    window.open(
      "https://mitsloanedtech.mit.edu/ai/basics/effective-prompts/",
      "_blank",
      "noopener,noreferrer"
    );
    // Close immediately
    setSubmenu(null);
    setSuppressContent(true);
    setLockedWidth(buttonWidth != null ? buttonWidth : naturalWidthRef.current);
    setIsAnimating(true);
    setOpen(false);
    if (onAction) onAction("tips");
  };

  const handleMakeBetter = () => {
    // Toggle the right-hand submenu for "Make my prompt better"
    setSubmenu((prev) => (prev === "make_better" ? null : "make_better"));
  };

  // Submenu click -> invoke parent action and close menus
  const handleSubmenuClick = (actionKey) => {
    // Close immediately
    setSubmenu(null);
    setSuppressContent(true);
    setLockedWidth(buttonWidth != null ? buttonWidth : naturalWidthRef.current);
    setIsAnimating(true);
    setOpen(false);
    // Fire action without waiting
    if (onAction) onAction(actionKey);
  };

  return (
    <div className="prompt-tools-wrapper" ref={wrapperRef}>
      {/* Backdrop removed per latest instruction */}
      <button
        type="button"
        className={`prompt-tools-button ${open ? "open" : ""} ${
          suppressContent ? "hide-content" : ""
        }`}
        onClick={handleToggle}
        title="Prompt Tools"
        ref={btnRef}
        style={{
          width: open ? 40 : isAnimating ? lockedWidth : undefined,
          padding: open ? 0 : undefined,
        }}
        onTransitionEnd={(e) => {
          if (e.propertyName === "width") {
            if (!open) {
              // Fully expanded back; reveal content again
              setSuppressContent(false);
              setIsAnimating(false);
              setLockedWidth(null); // clear inline width to allow natural sizing
            }
          }
        }}
      >
        <img src={wandIcon} alt="Prompt Tools" className="prompt-tools-icon" />
        <span className="prompt-tools-text">Prompt Tools</span>
        <span className="prompt-tools-x">
          <img src={plusWhite} alt="Close" />
        </span>
      </button>

      {open && (
        <div
          className={`prompt-tools-menu ${open ? "show" : ""} ${
            submenu === "make_better" ? "has-submenu" : ""
          }`}
          role="menu"
        >
          <div
            className="prompt-tools-menu-item"
            role="menuitem"
            onClick={handlePromptTemplates}
          >
            <img src={editIcon} alt="Templates" className="menu-icon" />
            <span className="menu-text">Prompt Templates</span>
            <span className="right-icons">
              <img src={caretRightIcon} alt="More" className="right-caret" />
            </span>
          </div>

          <div
            className="prompt-tools-menu-item"
            role="menuitem"
            onClick={handlePromptingTips}
          >
            <img src={infoIcon} alt="Tips" className="menu-icon" />
            <span className="menu-text">Prompting Tips</span>
            <span className="right-icons">
              <img
                src={externalLinkIcon}
                alt="External"
                className="external-link-icon"
              />
            </span>
          </div>

          <div
            className="prompt-tools-menu-item"
            role="menuitem"
            onClick={handleMakeBetter}
          >
            <img src={wandIcon} alt="Make Better" className="menu-icon" />
            <span className="menu-text">Make my prompt better</span>
            <span className="right-icons">
              <img src={caretRightIcon} alt="More" className="right-caret" />
            </span>
          </div>
          {submenu === "make_better" && (
            <div
              className={`prompt-tools-submenu show`}
              role="menu"
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className="submenu-item"
                role="menuitem"
                onClick={() => handleSubmenuClick("structure")}
              >
                <div className="submenu-header">
                  <img
                    src={improveIcon}
                    alt="Improve"
                    className="submenu-icon normal"
                  />
                  <img
                    src={improveIconWhite}
                    alt="Improve"
                    className="submenu-icon white"
                  />
                  <span className="submenu-title">
                    Improve structure & format
                  </span>
                </div>
                <div className="submenu-desc">
                  Apply basic markup and organization so the AI can read and
                  follow instructions more clearly
                </div>
              </div>
              <div
                className="submenu-item"
                role="menuitem"
                onClick={() => handleSubmenuClick("clarity")}
              >
                <div className="submenu-header">
                  <img
                    src={clarityIcon}
                    alt="Clarity"
                    className="submenu-icon normal"
                  />
                  <img
                    src={clarityIconWhite}
                    alt="Clarity"
                    className="submenu-icon white"
                  />
                  <span className="submenu-title">Add clarity & detail</span>
                </div>
                <div className="submenu-desc">
                  Make the prompt more specific, reduce ambiguity, and use
                  richer descriptive language
                </div>
              </div>
              <div
                className="submenu-item"
                role="menuitem"
                onClick={() => handleSubmenuClick("context")}
              >
                <div className="submenu-header">
                  <img
                    src={contextIcon}
                    alt="Context"
                    className="submenu-icon normal"
                  />
                  <img
                    src={contextIconWhite}
                    alt="Context"
                    className="submenu-icon white"
                  />
                  <span className="submenu-title">Suggest missing context</span>
                </div>
                <div className="submenu-desc">
                  Provide optional fill-in-the-blank sections where you can add
                  additional details
                </div>
              </div>
              <div
                className="submenu-item"
                role="menuitem"
                onClick={() => handleSubmenuClick("all")}
              >
                <div className="submenu-header">
                  <img
                    src={rocketIcon}
                    alt="All"
                    className="submenu-icon normal"
                  />
                  <img
                    src={rocketIconWhite}
                    alt="All"
                    className="submenu-icon white"
                  />
                  <span className="submenu-title">All of the above</span>
                </div>
                <div className="submenu-desc">Let me cook, baby!</div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PromptToolsButton;
